
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useIsMobile } from '@/hooks/use-mobile';
import { useEffect, useState } from 'react';

const ResearchPreview = () => {
  const isMobile = useIsMobile();
  const [refreshKey, setRefreshKey] = useState(Date.now());
  
  // Force refresh on mount and when mobile state changes
  useEffect(() => {
    console.log('ResearchPreview mounted - refreshing content');
    setRefreshKey(Date.now());
    
    // Add a meta refresh tag to force page reload if needed
    const metaRefresh = document.createElement('meta');
    metaRefresh.setAttribute('http-equiv', 'refresh');
    metaRefresh.setAttribute('content', '0;url=' + window.location.href);
    metaRefresh.setAttribute('id', 'meta-refresh-tag');
    
    // Only add if not already present and if we detect stale content
    if (!document.getElementById('meta-refresh-tag') && document.body.getAttribute('data-stale') === 'true') {
      document.head.appendChild(metaRefresh);
      document.body.removeAttribute('data-stale');
    }
  }, [isMobile]);

  return (
    <section className="py-12 sm:py-16 md:py-20 relative overflow-hidden" key={`research-preview-${refreshKey}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-2xl xs:text-3xl md:text-4xl font-bold mb-3 xs:mb-4">Research Initiatives</h2>
          <p className="text-delphi-light/70 text-sm xs:text-base sm:text-lg">
            Our quantitative trading strategy funds cutting-edge scientific research across disciplines, challenging conventional wisdom and fostering innovation.
          </p>
        </div>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 xs:gap-6">
          <div className="data-section p-4 xs:p-6 bg-gradient-to-br from-delphi-gray/20 to-transparent backdrop-blur-sm">
            <div className="mb-4 xs:mb-6">
              <span className="inline-block bg-delphi-red/20 text-delphi-red text-xs font-medium rounded px-2.5 py-1">Physics</span>
            </div>
            <h3 className="text-lg xs:text-xl font-bold mb-2 xs:mb-3">Implicit Dynamics of Cumulative Measurement</h3>
            <p className="text-delphi-light/60 text-xs xs:text-sm mb-4 xs:mb-6">Exploring the hypothesis that some Observable dynamics may be emergent consequences of the act of observation itself.</p>
            <Link to="/research" className="flex items-center text-delphi-red text-sm font-medium hover:underline">
              <span>Read More</span>
              <ArrowRight size={16} className="ml-1" />
            </Link>
          </div>
          
          <div className="data-section p-6 bg-gradient-to-br from-delphi-gray/20 to-transparent backdrop-blur-sm">
            <div className="mb-6">
              <span className="inline-block bg-delphi-red/20 text-delphi-red text-xs font-medium rounded px-2.5 py-1">Mathematics</span>
            </div>
            <h3 className="text-xl font-bold mb-3">Deterministic Emergence from Microscopic Chaos</h3>
            <p className="text-delphi-light/60 text-sm mb-6">
              Advancing mathematical framework for predicting emergent properties in complex adaptive systems.
            </p>
            <Link to="/research" className="flex items-center text-delphi-red text-sm font-medium hover:underline">
              <span>Read More</span>
              <ArrowRight size={16} className="ml-1" />
            </Link>
          </div>
          
          <div className="data-section p-6 bg-gradient-to-br from-delphi-gray/20 to-transparent backdrop-blur-sm">
            <div className="mb-6">
              <span className="inline-block bg-delphi-red/20 text-delphi-red text-xs font-medium rounded px-2.5 py-1">Market Theory</span>
            </div>
            <h3 className="text-xl font-bold mb-3">Option Expiration Dates</h3>
            <p className="text-delphi-light/60 text-sm mb-6">
              Investigating entropic forces and their effects on market dynamics during option expiration periods.
            </p>
            <Link to="/research" className="flex items-center text-delphi-red text-sm font-medium hover:underline">
              <span>Read More</span>
              <ArrowRight size={16} className="ml-1" />
            </Link>
          </div>
          
          <div className={`data-section p-4 xs:p-6 bg-gradient-to-br from-delphi-gray/20 to-transparent backdrop-blur-sm ${isMobile ? "" : "md:col-span-3 lg:col-span-1"}`}>
            <div className="mb-4 xs:mb-6">
              <span className="inline-block bg-delphi-red/20 text-delphi-red text-xs font-medium rounded px-2.5 py-1">Neuroscience & AI</span>
            </div>
            <h3 className="text-lg xs:text-xl font-bold mb-2 xs:mb-3">Biomimicry of Dragonfly Optical Lobes in Deep Neural Networks</h3>
            <p className="text-delphi-light/60 text-xs xs:text-sm mb-4 xs:mb-6">
              Exploring dragonfly optical lobe architecture to enhance trajectory prediction in artificial neural networks.
            </p>
            <Link to="/research" className="flex items-center text-delphi-red text-sm font-medium hover:underline">
              <span>Read More</span>
              <ArrowRight size={16} className="ml-1" />
            </Link>
          </div>
        </div>
        
        <div className="mt-8 sm:mt-10 md:mt-12 text-center">
          <Link to="/research" className="inline-flex items-center px-5 py-2.5 xs:px-6 xs:py-3 border border-delphi-gray/30 rounded-md text-delphi-light hover:bg-delphi-gray/10 transition-all">
            <span>View All Research</span>
            <ArrowRight size={16} className="ml-2" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ResearchPreview;
